import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useTenant } from '../tenant/TenantContext'
import { useToasts } from './Toasts'
import { usePerms } from '../auth/PermsContext'
import { useI18n } from '../i18n'
import { apiGet, apiPost } from '../utils/api'

type Endpoint = { id: number; name: string }
type Repo = { id: number; name: string; type: string }
type Target = { id: number; name: string; type: string; config?: Record<string, any> }
type Job = { id: number; created_at: string; state: string; payload?: Record<string, any>; result?: Record<string, any> }
type RestorePlanPreview = {
  restore_plan: Record<string, any>
  snapshot_id?: string
  backup_job_id?: number
  backup_created_at?: string
}

const SUPPORTED_TARGETS = new Set(['mysql', 'wordpress'])

const formatDate = (value?: string) => (value ? new Date(value).toLocaleString() : 'N/A')

type AutomationPlanProps = {
  t: (key: string, params?: Record<string, string | number>) => string
  plan: RestorePlanPreview | null
  planLoading: boolean
  planError: string | null
  selectedTarget: Target | null
}

const AutomationPlan = ({ t, plan, planLoading, planError, selectedTarget }: AutomationPlanProps) => {
  if (!selectedTarget) {
    return <p className="text-xs text-gray-500">{t('restore.instructions.select_target')}</p>
  }
  if (planLoading) {
    return <p className="text-xs text-gray-500">{t('common.loading')}</p>
  }
  if (planError) {
    return <p className="text-xs text-red-500">{planError}</p>
  }
  if (!plan) {
    return <p className="text-xs text-gray-500">{t('restore.plan.none')}</p>
  }
  const rp = plan.restore_plan || {}
  const snapshot = plan.snapshot_id || rp.snapshot_id
  const backupJobId = plan.backup_job_id ?? rp.backup_job_id
  const backupTimeRaw = plan.backup_created_at ?? rp.backup_created_at
  let backupTimeFormatted: string | null = null
  if (backupTimeRaw) {
    if (typeof backupTimeRaw === 'string') backupTimeFormatted = formatDate(backupTimeRaw)
    else if (backupTimeRaw instanceof Date) backupTimeFormatted = formatDate(backupTimeRaw.toISOString())
  }
  const toArray = (value: any): string[] => {
    if (!value) return []
    if (Array.isArray(value)) return value.map(item => String(item)).filter(Boolean)
    if (typeof value === 'string') return value ? [value] : []
    return [String(value)]
  }
  const restoredPaths = [...toArray(rp.site_path), ...toArray(rp.paths)]
  const extraPaths = toArray(rp.extra_paths)
  const preHooks = toArray(rp.restore_pre)
  const postHooks = toArray(rp.restore_post)
  return (
    <div className="text-xs space-y-1">
      {snapshot && (
        <div>
          {t('restore.plan.snapshot')}: <code>{snapshot}</code>
        </div>
      )}
      {backupJobId && (
        <div>
          {t('restore.plan.backup_job')}: #{backupJobId}
          {backupTimeFormatted ? ` - ${backupTimeFormatted}` : ''}
        </div>
      )}
      {restoredPaths.length > 0 && (
        <div>
          <div className="font-medium text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('restore.plan.paths')}</div>
          <ul className="list-disc list-inside space-y-1">
            {restoredPaths.map((item, idx) => (
              <li key={`plan-path-${idx}`}><code>{item}</code></li>
            ))}
          </ul>
        </div>
      )}
      {extraPaths.length > 0 && (
        <div>
          <div className="font-medium text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('restore.plan.extra_paths')}</div>
          <ul className="list-disc list-inside space-y-1">
            {extraPaths.map((item, idx) => (
              <li key={`plan-extra-${idx}`}><code>{item}</code></li>
            ))}
          </ul>
        </div>
      )}
      {preHooks.length > 0 && (
        <div>
          <div className="font-medium text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('restore.plan.hooks_pre')}</div>
          <ul className="list-disc list-inside space-y-1">
            {preHooks.map((item, idx) => (
              <li key={`plan-pre-${idx}`}><code>{item}</code></li>
            ))}
          </ul>
        </div>
      )}
      {postHooks.length > 0 && (
        <div>
          <div className="font-medium text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('restore.plan.hooks_post')}</div>
          <ul className="list-disc list-inside space-y-1">
            {postHooks.map((item, idx) => (
              <li key={`plan-post-${idx}`}><code>{item}</code></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}


export default function RestoreWizard() {
  const { t } = useI18n()
  const { current } = useTenant()
  const tenantId = current?.id
  const { push } = useToasts()
  const { has } = usePerms()

  const [endpoints, setEndpoints] = useState<Endpoint[]>([])
  const [endpointId, setEndpointId] = useState<number>(0)
  const [repositories, setRepositories] = useState<Repo[]>([])
  const [repositoryId, setRepositoryId] = useState<number>(0)
  const [targets, setTargets] = useState<Target[]>([])
  const [targetId, setTargetId] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [latestJob, setLatestJob] = useState<Job | null>(null)
  const [note, setNote] = useState('restore-wizard')
  const [plan, setPlan] = useState<RestorePlanPreview | null>(null)
  const [planLoading, setPlanLoading] = useState(false)
  const [planError, setPlanError] = useState<string | null>(null)

  useEffect(() => {
    if (!tenantId) return
    void (async () => {
      try {
        const eps = await apiGet(`/tenants/${tenantId}/endpoints`)
        setEndpoints(eps)
        if (!endpointId && eps.length) setEndpointId(eps[0].id)
      } catch (e: any) {
        push({ text: e?.message || 'Failed to load endpoints', kind: 'error' })
      }
    })()
  }, [tenantId])

  useEffect(() => {
    if (!tenantId) return
    void (async () => {
      try {
        const repos = await apiGet(`/tenants/${tenantId}/repositories`)
        setRepositories(repos)
        if (!repositoryId && repos.length) setRepositoryId(repos[0].id)
      } catch (e: any) {
        push({ text: e?.message || 'Failed to load repositories', kind: 'error' })
      }
    })()
  }, [tenantId])

  useEffect(() => {
    if (!endpointId) {
      setTargets([])
      setTargetId(0)
      return
    }
    void (async () => {
      setLoading(true)
      try {
        const list: Target[] = await apiGet(`/endpoints/${endpointId}/targets`)
        const filtered = list.filter(tgt => SUPPORTED_TARGETS.has(tgt.type))
        setTargets(filtered)
        if (filtered.length && !filtered.find(t => t.id === targetId)) {
          setTargetId(filtered[0].id)
        }
      } catch (e: any) {
        push({ text: e?.message || 'Failed to load targets', kind: 'error' })
      } finally {
        setLoading(false)
      }
    })()
  }, [endpointId])

  useEffect(() => {
    if (!tenantId || !endpointId || !targetId) {
      setLatestJob(null)
      return
    }
    void (async () => {
      try {
        const params = new URLSearchParams({
          task_type: 'backup',
          endpoint_id: String(endpointId),
          limit: String(25),
        })
        const jobs: Job[] = await apiGet(`/tenants/${tenantId}/jobs?${params.toString()}`)
        const match = jobs.find(job => {
          const payload = job.payload || {}
          return Number(payload.target_id) === targetId || Number(payload.target?.id) === targetId
        })
        setLatestJob(match ?? null)
      } catch {
        setLatestJob(null)
      }
    })()
  }, [tenantId, endpointId, targetId])

  useEffect(() => {
    if (!endpointId || !repositoryId || !targetId) {
      setPlan(null)
      setPlanError(null)
      return
    }
    void (async () => {
      setPlanLoading(true)
      setPlanError(null)
      try {
        const body: Record<string, any> = {
          repository_id: repositoryId,
          target_id: targetId,
        }
        const snapshot = latestJob?.result?.snapshot_id
        if (snapshot) body.snapshot_id = snapshot
        const data: RestorePlanPreview = await apiPost(`/restore/plan-preview?endpoint_id=${endpointId}`, body)
        setPlan(data)
      } catch (e: any) {
        setPlan(null)
        setPlanError(e?.message || t('restore.plan.none'))
      } finally {
        setPlanLoading(false)
      }
    })()
  }, [endpointId, repositoryId, targetId, latestJob?.result?.snapshot_id, t])

  const selectedTarget = useMemo(() => targets.find(t => t.id === targetId) || null, [targets, targetId])
  const selectedRepo = useMemo(() => repositories.find(r => r.id === repositoryId) || null, [repositories, repositoryId])
  const contextTags = useMemo(() => {
    if (!selectedTarget) return []
    const tags = [`target:${selectedTarget.type}`]
    if (selectedRepo) tags.push(`repository:${selectedRepo.type}`)
    if (endpointId) tags.push(`endpoint:${endpointId}`)
    return tags
  }, [selectedTarget, selectedRepo, endpointId])

  const runRestoreJob = async (e: FormEvent) => {
    e.preventDefault()
    if (!has('jobs.write')) {
      push({ text: t('jobs.errors.enqueue_failed'), kind: 'error' })
      return
    }
    if (!endpointId || !repositoryId || !targetId) {
      push({ text: t('restore.errors.missing_selection'), kind: 'error' })
      return
    }
    try {
      const payload: Record<string, any> = { note }
      if (plan?.snapshot_id) payload.snapshot_id = plan.snapshot_id
      else if (latestJob?.result?.snapshot_id) payload.snapshot_id = latestJob.result.snapshot_id
      await apiPost('/jobs/run', {
        endpoint_id: endpointId,
        repository_id: repositoryId,
        target_id: targetId,
        task_type: 'restore',
        payload,
      })
      push({ text: t('restore.feedback.enqueue_success'), kind: 'success' })
    } catch (e: any) {
      push({ text: e?.message || t('restore.errors.enqueue_failed'), kind: 'error' })
    }
  }

  const renderManualInstructions = () => {
    if (!selectedTarget) return null
    const cfg = selectedTarget.config || {}
    if (selectedTarget.type === 'wordpress') {
      return (
        <div className="space-y-2 text-sm">
          <p>{t('restore.wordpress.step_intro')}</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>{t('restore.wordpress.step_env')}</li>
            <li>
              <code className="block bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs">
                restic restore latest --target ./restore-wordpress --tag {contextTags.join(' --tag ')} --include "{cfg.site_path || '/var/www/html'}" --include "*.sql"
              </code>
            </li>
            <li>{t('restore.wordpress.step_sync_files', { path: cfg.site_path || '/var/www/html' })}</li>
            <li>{t('restore.wordpress.step_db', { db_name: cfg.db_name || 'wordpress' })}</li>
          </ol>
        </div>
      )
    }
    return (
      <div className="space-y-2 text-sm">
        <p>{t('restore.mysql.step_intro')}</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>{t('restore.mysql.step_env')}</li>
          <li>
            <code className="block bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs">
              restic restore latest --target ./restore-mysql --tag {contextTags.join(' --tag ')} --include "*.sql"
            </code>
          </li>
          <li>{t('restore.mysql.step_find_dump')}</li>
          <li>{t('restore.mysql.step_import', { db_name: cfg.db_name || 'database' })}</li>
        </ol>
      </div>
    )
  }

  return (
    <div className="card">
      <h2 className="font-semibold mb-3">{t('restore.title')}</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t('restore.subtitle')}</p>

      <form className="grid md:grid-cols-2 gap-4 mb-6" onSubmit={runRestoreJob}>
        <label className="flex flex-col text-sm">
          <span className="mb-1 font-medium">{t('table.endpoint')}</span>
          <select className="input" value={endpointId} onChange={e => setEndpointId(Number(e.target.value))} disabled={!has('jobs.write')}>
            {endpoints.map(ep => <option key={ep.id} value={ep.id}>{ep.name}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 font-medium">{t('restore.fields.repository')}</span>
          <select className="input" value={repositoryId} onChange={e => setRepositoryId(Number(e.target.value))} disabled={!has('jobs.write')}>
            {repositories.map(repo => <option key={repo.id} value={repo.id}>{repo.name} ({repo.type})</option>)}
          </select>
        </label>
        <label className="flex flex-col text-sm md:col-span-2">
          <span className="mb-1 font-medium">{t('table.target')}</span>
          {loading ? (
            <span className="text-xs text-gray-500">{t('common.loading')}</span>
          ) : (
            <select className="input" value={targetId} onChange={e => setTargetId(Number(e.target.value))} disabled={!has('jobs.write')}>
              {targets.map(tgt => <option key={tgt.id} value={tgt.id}>{tgt.name} ({tgt.type})</option>)}
            </select>
          )}
          {!targets.length && <span className="text-xs text-gray-500 mt-1">{t('restore.feedback.no_targets')}</span>}
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 font-medium">{t('restore.fields.note')}</span>
          <input className="input" value={note} onChange={e => setNote(e.target.value)} disabled={!has('jobs.write')} />
        </label>
        <div className="flex items-end">
          <button className="btn" type="submit" disabled={!has('jobs.write') || !selectedTarget}>
            {t('restore.actions.enqueue_restore')}
          </button>
        </div>
      </form>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border rounded p-4 bg-gray-50 dark:bg-gray-900/40">
          <h3 className="font-semibold text-sm mb-2">{t('restore.latest_backup.title')}</h3>
          {latestJob ? (
            <div className="text-xs space-y-1">
              <div>{t('restore.latest_backup.when')}: {formatDate(latestJob.created_at)}</div>
              <div>{t('restore.latest_backup.state')}: {latestJob.state}</div>
              {latestJob.result?.snapshot_id && <div>snapshot_id: <code>{latestJob.result.snapshot_id}</code></div>}
              {selectedTarget?.type === 'wordpress' && selectedTarget.config?.site_path && (
                <div>{t('restore.latest_backup.site_path')}: <code>{selectedTarget.config.site_path}</code></div>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500">{t('restore.latest_backup.none')}</p>
          )}
        </div>
        <div className="border rounded p-4 bg-gray-50 dark:bg-gray-900/40">
          <h3 className="font-semibold text-sm mb-2">{t('restore.automation.title')}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('restore.automation.description')}</p>
          <AutomationPlan t={t} plan={plan} planLoading={planLoading} planError={planError} selectedTarget={selectedTarget} />
        </div>
      </div>
      <div className="border rounded p-4 bg-gray-50 dark:bg-gray-900/40 mt-4">
        <h3 className="font-semibold text-sm mb-2">{t('restore.manual.title')}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('restore.manual.description')}</p>
        {selectedTarget ? renderManualInstructions() : <p className="text-xs text-gray-500">{t('restore.instructions.select_target')}</p>}
      </div>
    </div>
  )
}
